import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import React, {useMemo, useEffect, useState} from 'react';

import {Dataset} from '../../../types/dataset/Dataset';
import {RootState, useAppDispatch} from '../../../store';
import {toast} from '../../../base-components/StudioToast';
import {dataPrepareActions} from '../../../store/dataPrepare';
import TimerWaiter from '../../../base-components/StudioTimerWaiter/StudioTimerWaiter';
import {
  createDataset,
  generateTicketId,
  updateDatasetMetadata,
  deleteDatasetMetadata,
} from '../../../store/dataService';

import './CreateDatasetStep.scss';

const {setName, setDescription} = dataPrepareActions;

type CreateDatasetStepProps = {
  projectId: string;
  dataPreparationContainerId?: string;
  onForward: () => void;
};

export const CreateDatasetStep = ({onForward}: CreateDatasetStepProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [initialized, setInitialized] = useState(false);
  const name = useSelector((state: RootState) => state.dataPrepare.name);
  const datasetId = useSelector((state: RootState) => state.dataset.data?.id);
  const description = useSelector((state: RootState) => state.dataPrepare.description);

  const metadataInfo = useSelector((state: RootState) => state.datasetMetadata.info);
  const methodId = useMemo(() => metadataInfo?.id, [metadataInfo]);

  useEffect(
    function createDatasetInitialization() {
      if (!initialized) {
        setInitialized(true);

        (async () => {
          try {
            if (datasetId && methodId) {
              await dispatch(
                deleteDatasetMetadata({
                  datasetId,
                  metadataIds: methodId,
                })
              );
            }

            const {payload} = await dispatch(createDataset());

            const finalName = name?.trim();
            const finalDescription = description?.trim() || '';

            if (!finalName) {
              console.warn('Dataset name in memory is empty after trimming.');
              throw new Error();
            }

            if (name !== finalName) {
              dispatch(setName(finalName));
            }

            if (finalDescription !== description) {
              dispatch(setDescription(finalDescription));
            }

            await dispatch(
              updateDatasetMetadata({
                flow: 'info',
                datasetId: (payload as Dataset)?.id,
                payload: [
                  {
                    name: finalName,
                    id: generateTicketId(),
                    type: 'annotation/common_info',
                    description: finalDescription,
                  },
                ],
              })
            );

            onForward();
          } catch (error) {
            toast.error(intl.formatMessage({id: 'dataPrep.datasetCreationError'}));
          }
        })();
      }
    },
    [intl, initialized, datasetId, methodId, name, description, dispatch, onForward]
  );

  return (
    <div className="create-dataset-step">
      <TimerWaiter
        loadingText={intl.formatMessage({
          id: 'dataPrep.dataset.creation',
        })}
      />
    </div>
  );
};
